/**
 * Componente de campo ID do Node somente leitura + campo editável de Apelido
 * Exibe o ID do node com botão para copiar e campo opcional para apelido
 * Usa forwardRef + useImperativeHandle para expor getNickname() ao componente pai
 * 
 * RELEASE: 2.0.0 - 2026-03-07 - Adicionado campo de apelido (nickname) com ref API
 */

import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';

/** Interface exposta via ref para o componente pai */
export interface NodeIdFieldRef {
  /** Retorna o apelido atual (trimmed) ou undefined se vazio */
  getNickname: () => string | undefined;
}

interface NodeIdFieldProps {
  /** ID do node (somente leitura) */
  nodeId: string | undefined;
  /** Valor inicial do apelido (carregado do config do node) */
  defaultNickname?: string;
}

/**
 * Campo que exibe o ID do node com opção de copiar
 * e campo editável de apelido para identificação na tela de resultados
 * 
 * Uso: const ref = useRef<NodeIdFieldRef>(null);
 *      <NodeIdField ref={ref} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />
 *      // No handleSave: nickname: ref.current?.getNickname()
 */
export const NodeIdField = forwardRef<NodeIdFieldRef, NodeIdFieldProps>(
  ({ nodeId, defaultNickname = '' }, ref) => {
    const [nickname, setNickname] = useState(defaultNickname);

    /** Sincroniza quando o node muda (ex: abrir modal para outro node) */
    useEffect(() => {
      setNickname(defaultNickname);
    }, [defaultNickname]);

    /** Expõe método para o componente pai ler o apelido */
    useImperativeHandle(ref, () => ({
      getNickname: () => nickname.trim() || undefined,
    }), [nickname]);

    if (!nodeId) return null;

    /** Copia o ID do node para a área de transferência */
    const handleCopy = () => {
      navigator.clipboard.writeText(nodeId);
      toast.success('ID copiado!');
    };

    return (
      <div className="space-y-3 mb-4">
        {/* ID do Node (somente leitura) */}
        <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg border">
          <span className="text-xs font-medium text-muted-foreground">ID do Node:</span>
          <code className="text-xs bg-background px-2 py-0.5 rounded font-mono flex-1 truncate">
            {nodeId}
          </code>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 px-2"
            onClick={handleCopy}
            title="Copiar ID"
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>

        {/* Campo de Apelido (editável) */}
        <div className="space-y-1">
          <Label className="text-xs">Apelido do Node (opcional)</Label>
          <Input
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="Ex: Pergunta inicial, Coleta de nome, Verificação CPF..."
            maxLength={60}
            className="h-8 text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Nome amigável exibido como cabeçalho na tabela de resultados
          </p>
        </div>
      </div>
    );
  }
);

// DisplayName para debugging
NodeIdField.displayName = 'NodeIdField';
